import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
	fetchPlaylists,
	selectPlaylistByIds,
	selectPlaylistsByContext,
} from "./slice";
import { parsePlaylistFilters } from "./filters";
import { updateSearchParamsWithFilters } from "../filters/filters";
import Layout from "../../ui/Layout";
import NavMain from "../../app/NavMain";
import ScrollablePage from "../../ui/ScrollablePage";
import Paper from "../../ui/Paper";
import ErrorMsg from "../../ui/ErrorMsg";
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner";
import Button from "../../ui/Button";

import { Header, Overview } from "../../ui/PageHeader";
import PlaylistsTable from "./PlaylistsTable";

const PAGE_SIZE = 200; // Default initial and subsequent page size

/* Playlists renders a page containing a filter- and sort-able table
 * of all playlists matching the criteria in the current URL.  */
const Playlists = () => {
	const dispatch = useDispatch();

	// we get any matching playlists
	const playlistMatches = useSelector((state) =>
		selectPlaylistsByContext(state, "root"),
	);
	// We get the full objects from the ids that match
	const playlists = useSelector((state) =>
		selectPlaylistByIds(state, playlistMatches?.ids || []),
	);

	// Parse parameters into filter object (which drives filters UI state)
	// Note: We are using the URL as the "state" here, and updates
	// perform a history replacement (so as not to mess up "back" button
	// behaviour too much - though we may want to apply a heuristic like
	// small changes replace history, big changes push history)
	const location = useLocation();

	const navigate = useNavigate();
	const urlParams = new URLSearchParams(location.search);
	const filter = parsePlaylistFilters(urlParams);
	updateSearchParamsWithFilters(urlParams, filter);

	useEffect(() => {
		dispatch(
			fetchPlaylists({
				filter,
				page: { offset: 0, limit: PAGE_SIZE },
				context: ["root"],
			}),
		);
	}, [urlParams.toString()]);

	// How we apply a filter update. Filter state is entirely in the URL query parameters
	const setFilter = (filter = {}) => {
		const updUrlParams = updateSearchParamsWithFilters(urlParams, filter);
		navigate({
			pathname: location.pathname,
			search: `?${updUrlParams}`,
		});
	};

	// How many more are available to load?
	const loadableCount = Math.min(
		PAGE_SIZE,
		playlists?.length && playlistMatches?.page?.total_count
			? playlistMatches.page.total_count - playlists.length
			: 50,
	);

	// Event handler for 'Show xxx more' button that was clicked
	const handleLoadMore = () => {
		const lastIndex = playlists?.length;

		const page = {
			offset: lastIndex,
			limit: loadableCount,
		};
		dispatch(fetchPlaylists({ filter, page, context: ["root"] }));
	};

	return (
		<Layout>
			<NavMain key="nav-left" />
			<Helmet>
				<title>Schedules - Astrosite</title>
			</Helmet>
			<ScrollablePage>
				{playlistMatches?._loadStatus === "loading" && (
					<Backdrop>
						<LoadingSpinner />
					</Backdrop>
				)}
				<Header>
					<Overview>
						<h1>Schedules</h1>
						<p>
							{playlists.length > 0 && (
								<span>
									Showing <strong>{playlistMatches?.page?.end_index}</strong>
									{playlistMatches?.page?.total_count && (
										<span>
											{" "}
											of{" "}
											<strong className="count total_count">
												{playlistMatches.page.total_count}
											</strong>
										</span>
									)}{" "}
									schedules
								</span>
							)}
						</p>
					</Overview>
				</Header>
				<Header>
					<ErrorMsg
						msg="Please note: The Schedules feature is currently unavailable while multi-modal telescope operation support is added"
						showIcon
					/>
				</Header>

				{playlistMatches?._loadStatus == "rejected" && (
					<ErrorMsg
						msg={"There was a problem fetching the schedules"}
						showIcon={true}
					/>
				)}

				<Paper>
					<PlaylistsTable
						playlists={playlists}
						filter={filter}
						setFilter={setFilter}
					/>
					{playlists?.length == 0 && playlistMatches?._loadStatus == "idle" && (
						<p>There are no schedules matching your criteria.</p>
					)}
					<footer className="actions">
						{playlists.length > 0 &&
							playlists.length < playlistMatches?.page?.total_count && (
								<>
									<Button
										onClick={handleLoadMore}
										disabled={playlistMatches?._loadStatus == "loading"}
									>
										{playlistMatches?._loadStatus == "loading"
											? "Loading..."
											: `Show ${loadableCount} more`}
									</Button>
								</>
							)}
					</footer>
				</Paper>
				<div></div>
			</ScrollablePage>
		</Layout>
	);
};

export default Playlists;
